import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { combineLatest, from, iif, of } from 'rxjs';
import { catchError, filter, first, map, switchMap } from 'rxjs/operators';
import { AuthenticationRestService, BootRestService, LocalUnitRestService, MarketMerchantRestService, PaymentsMarketRestService, internalServerError, pageNotFound } from 'sp-arch-lib';
import { environment } from 'sp-mark-src/environments/environment';
import * as bootActions from 'sp-mark/feature-modules/boot/redux/boot.actions';
import * as bootSelectors from 'sp-mark/feature-modules/boot/redux/index';
import * as paymentsActions from 'sp-mark/feature-modules/payments/redux/payments.actions';
import { routingPathsConstants } from 'sp-mark/models/routing-paths.constants';
import { AppState } from 'sp-mark/redux';
import * as routerActions from 'sp-mark/redux/router/router.actions';
import { MerchantStateType, closeDialog, generateLocalUnits, openDialog } from 'sp-ui-lib';

const LABELS = {
  CLOSE: 'SHARED.BUTTON.CLOSE',
  CONGRATS: 'SHARED.DIALOG.CONGRATS',
  DIALOG_ALERT_TITLE: 'SHARED.DIALOG.ALERT_TITLE',
  ERROR: 'SHARED.DIALOG.ERROR',
  GENERIC_ERROR: 'SHARED.DIALOG.GENERIC_ERROR'
};

@Injectable()
export class BootEffects {
  private readonly httpUnauthorized = 401;

  /**
   * Started by boot-container
   */
  startBoot$ = createEffect(() =>
    this.actions$.pipe(
      ofType(bootActions.startBoot),
      switchMap(() => [
        bootActions.mandatoryData(),
        bootActions.checkSteps({flagCheckInsolventMerchant: false})
      ])
    )
  );

  /**
   * Retrieve mandatory data
   */
  mandatoryData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(bootActions.mandatoryData),
      switchMap(() => [
        bootActions.retrieveLoggedMerchant(),
        bootActions.retrieveLocalUnits(),
      ])
    )
  );

  /**
   * Check propedeutical steps
   */
  checkSteps$ = createEffect(() =>
    this.actions$.pipe(
      ofType(bootActions.checkSteps),
      switchMap(action =>
        combineLatest([
          this.appState$.select(bootSelectors.selectLocalUnits),
          this.appState$.select(bootSelectors.selectLoggedMerchant)
        ]).pipe(
          filter(([localUnits, loggedMerchant]) => !!localUnits && !!loggedMerchant),
          first(),
          filter(([, loggedMerchant]) => (action.flagCheckInsolventMerchant && loggedMerchant?.state === 'INSOLVENTE') || !action.flagCheckInsolventMerchant),
        )
      ),
      switchMap(() =>
        this.bootService.checkSteps()
          .pipe(
            switchMap(steps =>
              iif(() => !steps.length,
                from([
                  routerActions.navigateTo(routingPathsConstants.DASHBOARD),
                  bootActions.setCurrentStepIndex({currentStepIndex: 0}),
                  bootActions.runStep({step: steps[0]}),
                  bootActions.checkStepsSuccess({steps})
                ]),
                from([
                  bootActions.setCurrentStepIndex({currentStepIndex: 0}),
                  bootActions.runStep({step: steps[0]}),
                  bootActions.checkStepsSuccess({steps})
                ])
              )
            ),
            catchError((error: HttpErrorResponse) => of(bootActions.loginFailure({error}))),
          )
      ),
      catchError(() => of(bootActions.checkStepsFailure()))
    )
  );

  /**
   * Step for insolvent merchant
   */
  stepInsolvedMerchant$ = createEffect(() =>
    this.actions$
      .pipe(
        ofType(bootActions.runStep),
        filter(action => action.step?.type === 'INSOLVENTE'),
        map(() => routerActions.navigateTo(routingPathsConstants.INSOLVENT_MERCHANT))
      )
  );

  /**
   * Step for insolvent merchant
   */
  stepPDFAccepter$ = createEffect(() =>
    this.actions$
      .pipe(
        ofType(bootActions.runStep),
        filter(action => action.step?.type === 'PDF_ACCEPTER'),
        map(() => routerActions.navigateTo(routingPathsConstants.PDF_ACCEPTER))
      )
  );


  /**
   * Accept/Skip step
   */
  resolveStep$ = createEffect(() =>
    this.actions$
      .pipe(
        ofType(bootActions.resolveStep),
        switchMap(action =>
          this.bootService.resolveStep(action.resolvedStep)
            .pipe(
              map(() => routerActions.navigateTo(routingPathsConstants.BOOT)),
              catchError(() => of(bootActions.resolveStepFailure()))
            )
        )
      )
  );

  doLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(bootActions.doLogin),
      map(props => props.loginData),
      switchMap(loginData =>
        this.authenticationRestService.login({username: loginData.username, password: loginData.password})
          .pipe(
            switchMap(authenticationRestresponse =>
              iif(() => loginData.flagRememberMe,
                from([
                  bootActions.rememberMe({username: loginData.username}),
                  bootActions.loginSuccess({
                    userInformation: {
                      username: authenticationRestresponse.username
                    }
                  })
                ]),
                from([
                  bootActions.forgetMe(),
                  bootActions.loginSuccess({
                    userInformation: {
                      username: authenticationRestresponse.username
                    }
                  })
                ])
              )
            ),
            catchError((error: HttpErrorResponse) => of(bootActions.loginFailure({error}))),
          )
      )
    )
  );

  /**
   * Navigate to boot after login
   */
  loginSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(bootActions.loginSuccess),
      map(() => routerActions.navigateTo(routingPathsConstants.BOOT))
    )
  );

  loginFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(bootActions.loginFailure),
      map(action => action.error),
      filter(error => error.status === this.httpUnauthorized),
      switchMap(error =>
        iif(() => error.error.message === 'INSOLVENTE',
          of(openDialog({
            dialogData: {
              title: LABELS.DIALOG_ALERT_TITLE,
              message: ['SHARED.LOGIN.INSOLVENT_MERCHANT'],
              buttons: [{
                label: LABELS.CLOSE,
                icon: '',
                callback: () => this.appState$.dispatch(closeDialog())
              }]
            }
          })),
          of(openDialog({
            dialogData: {
              title: LABELS.DIALOG_ALERT_TITLE,
              message: ['SHARED.LOGIN.WRONG_USER_OR_PWD'],
              buttons: [{
                label: LABELS.CLOSE,
                icon: '',
                callback: () => this.appState$.dispatch(closeDialog())
              }]
            }
          }))
        )
      )
    )
  );

  retrieveLoggedMerchant$ = createEffect(() =>
    this.actions$.pipe(
      ofType(bootActions.retrieveLoggedMerchant),
      switchMap(() =>
        this.merchantsRestService.getLoggedMerchant()
          .pipe(
            switchMap(merchantsResponse =>
              iif(() => !!merchantsResponse,
                of(bootActions.retrieveLoggedMerchantSuccess({
                  merchant: {
                    accountHolder: merchantsResponse.accountHolder,
                    cap: merchantsResponse.cap,
                    crmId: merchantsResponse.crmId,
                    category: merchantsResponse.category,
                    dateCreation: new Date(merchantsResponse.dateCreation),
                    accountHolderName: merchantsResponse.accountHolderName,
                    accountHolderSurname: merchantsResponse.accountHolderSurname,
                    email: merchantsResponse.email,
                    id: merchantsResponse.id,
                    internalEmail: merchantsResponse.internalEmail,
                    legalAddress: merchantsResponse.legalAddress,
                    legalCity: merchantsResponse.legalCity,
                    logoPath: `${environment.markerResourceUrl}/market/img/merchant/${merchantsResponse.uniqueId}/${merchantsResponse.logoPath}`,
                    name: merchantsResponse.name,
                    note: merchantsResponse.note,
                    pec: merchantsResponse.pec,
                    province: merchantsResponse.province,
                    sdi: merchantsResponse.sdi,
                    sector: merchantsResponse.sector,
                    services: merchantsResponse.services
                      ? Object.keys(merchantsResponse.services)
                        .map(value => `${value} x ${merchantsResponse.services[value]}`)
                      : null,
                    state: merchantsResponse.state as MerchantStateType,
                    subsector: merchantsResponse.subsector,
                    taxIdCode: merchantsResponse.taxIdCode,
                    telephone: merchantsResponse.telephone,
                    uniqueId: merchantsResponse.uniqueId,
                    vatNumber: merchantsResponse.vatNumber,
                    description: merchantsResponse.description,
                    shortDescription: merchantsResponse.shortDescription,
                    keywords: merchantsResponse.keywords,
                    website: merchantsResponse.website,
                  }
                })),
                of(bootActions.retrieveLoggedMerchantFailure())
              )
            ),
            catchError(() => of(bootActions.retrieveLoggedMerchantFailure()))
          )
      )
    )
  );

  retrieveLocalUnits$ = createEffect(() =>
    this.actions$.pipe(
      ofType(bootActions.retrieveLocalUnits),
      switchMap(() => this.localUnitRestService.getAllLocalUnits()
        .pipe(
          switchMap(localUnits =>
            iif(() => !!localUnits && localUnits.length > 0,
              of(bootActions.retrieveLocalUnitsSuccess({
                localUnits: generateLocalUnits(localUnits)
              })),
              of(bootActions.retrieveLocalUnitsFailure())
            )
          ),
          catchError(() => of(bootActions.retrieveLocalUnitsFailure()))
        )
      )
    )
  );

  rememberMe$ = createEffect(() =>
      this.actions$.pipe(
        ofType(bootActions.rememberMe),
        map(user => window.localStorage.setItem('smallPayUsername', user.username))
      ),
    {dispatch: false}
  );

  forgetMe$ = createEffect(() =>
      this.actions$.pipe(
        ofType(bootActions.forgetMe),
        map(() => window.localStorage.removeItem('smallPayUsername'))
      ),
    {dispatch: false}
  );

  internalServerError$ = createEffect(() =>
    this.actions$.pipe(
      ofType(internalServerError),
      map(() =>
        openDialog({
          dialogData: {
            title: LABELS.ERROR,
            message: ['SHARED.DIALOG.ERRORS.500'],
            buttons: [{
              label: LABELS.CLOSE,
              icon: '',
              callback: () => {
                this.appState$.dispatch(closeDialog());
              }
            }]
          }
        })
      )
    )
  );

  pageNotFound$ = createEffect(() =>
    this.actions$.pipe(
      ofType(pageNotFound),
      map(() =>
        openDialog({
          dialogData: {
            title: LABELS.ERROR,
            message: ['SHARED.DIALOG.ERRORS.404'],
            buttons: [{
              label: LABELS.CLOSE,
              icon: '',
              callback: () => this.appState$.dispatch(closeDialog())
            }]
          }
        })
      )
    )
  );

  proceedToPayUnpaidInstallments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(bootActions.proceedToPayUnpaidInstallments),
      switchMap(action =>
        this.paymentsMarketRestService.proceedToPayUnpaidInstallments(action.payments)
          .pipe(
            switchMap(response =>
              iif(() => !!response && response.state === 'OK',
                of(bootActions.proceedToPayUnpaidInstallmentsSuccess({
                  response,
                  filters: action.filters,
                  totalInsolventPayments: action.totalInsolventPayments
                })),
                of(bootActions.proceedToPayUnpaidInstallmentsFailure({errorDescription: response.errorDescription}))
              ),
            ),
            catchError(response => of(bootActions.proceedToPayUnpaidInstallmentsFailure({errorDescription: response.errorDescription})))
          )
      )
    )
  );

  proceedToPayUnpaidInstallmentsSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(bootActions.proceedToPayUnpaidInstallmentsSuccess),
      map(action =>
        action.totalInsolventPayments === action.response.rowsInvolved
          ? openDialog({
            dialogData: {
              title: LABELS.CONGRATS,
              message: ['BOOT.INSOLVENT_PAYMENTS_PAID'],
              buttons: [{
                label: LABELS.CLOSE,
                icon: '',
                callback: () => {
                  this.appState$.dispatch(closeDialog());
                  this.appState$.dispatch(bootActions.checkSteps({flagCheckInsolventMerchant: false}));
                }
              }]
            }
          })
          : openDialog({
            dialogData: {
              title: LABELS.DIALOG_ALERT_TITLE,
              message: [
                `Pagamenti elaborati: ${action.response.rowsInvolved}`,
                `Pagamenti eseguiti: ${action.response.rowsPaid}`,
                `Pagamenti insoluti: ${action.response.rowsInvolved - action.response.rowsPaid}`
              ],
              buttons: [{
                label: LABELS.CLOSE,
                icon: '',
                callback: () => {
                  this.appState$.dispatch(closeDialog());
                  this.appState$.dispatch(paymentsActions.retrievePayments({paymentRequestInterface: action.filters}));
                }
              }]
            }
          })
      )
    )
  );

  proceedToPayUnpaidInstallmentsFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(bootActions.proceedToPayUnpaidInstallmentsFailure),
      map(action => openDialog({
        dialogData: {
          title: LABELS.ERROR,
          message: [action.errorDescription || LABELS.GENERIC_ERROR],
          buttons: [{
            label: LABELS.CLOSE,
            icon: '',
            callback: () => this.appState$.dispatch(closeDialog())
          }]
        }
      }))
    )
  );

  constructor(
    private readonly actions$: Actions,
    private appState$: Store<AppState>,
    private readonly authenticationRestService: AuthenticationRestService,
    private readonly bootService: BootRestService,
    private readonly localUnitRestService: LocalUnitRestService,
    private readonly merchantsRestService: MarketMerchantRestService,
    private readonly paymentsMarketRestService: PaymentsMarketRestService
  ) {
  }
}
